import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";

const RET_BG = (v) => {
  if (v >= 75) return "bg-emerald-500 text-white";
  if (v >= 50) return "bg-blue-500 text-white";
  if (v >= 25) return "bg-amber-400 text-amber-900";
  return "bg-slate-200 text-slate-700";
};

export default function PortalCohorts() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/clinic/analytics/cohorts")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <PageShell
      eyebrow="Analytics · Cohort Analytics"
      title="Track retention and recurring engagement over time."
      intro="Operational utilization, engagement patterns, and recurring program participation only. Not clinical progression or medical outcomes."
      testId="cohorts-page"
    >
      {!data ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : data.cohorts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          No consumer cohorts to analyze yet. Cohort data will appear after consumers participate in workflow requests.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Kpi label="Total consumers" value={data.total_consumers} />
            <Kpi label="Avg 30-day retention" value={`${data.avg_retention_30}%`} />
            <Kpi label="Avg 60-day retention" value={`${data.avg_retention_60}%`} />
            <Kpi label="Avg 90-day retention" value={`${data.avg_retention_90}%`} />
          </div>

          {/* Retention curves */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Retention curves by cohort
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.cohorts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="cohort" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} unit="%" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="retention_30_pct" name="30-day" stroke="#16a34a" strokeWidth={2} />
                <Line type="monotone" dataKey="retention_60_pct" name="60-day" stroke="#2563eb" strokeWidth={2} />
                <Line type="monotone" dataKey="retention_90_pct" name="90-day" stroke="#7c3aed" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Heat map */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Cohort heat map
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Cohort (signup month)</th>
                    <th className="py-2 px-3">Size</th>
                    <th className="py-2 px-3 text-center">30-day</th>
                    <th className="py-2 px-3 text-center">60-day</th>
                    <th className="py-2 px-3 text-center">90-day</th>
                    <th className="py-2 px-3 text-center">Avg workflows / consumer</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.map((c) => (
                    <tr key={c.cohort} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cohort-row-${c.cohort}`}>
                      <td className="py-2 pr-3 font-semibold text-slate-900">{c.cohort}</td>
                      <td className="py-2 px-3 text-slate-700">{c.size}</td>
                      <td className="py-2 px-3 text-center"><HeatCell v={c.retention_30_pct} /></td>
                      <td className="py-2 px-3 text-center"><HeatCell v={c.retention_60_pct} /></td>
                      <td className="py-2 px-3 text-center"><HeatCell v={c.retention_90_pct} /></td>
                      <td className="py-2 px-3 text-center font-mono text-slate-700">{c.avg_workflows_per_consumer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5 text-[11px] text-slate-500 leading-relaxed">
            <b>Reminder:</b> Retention values reflect repeat workflow activity per consumer in the program — they are
            operational engagement indicators only and do not represent clinical progress.
          </div>
        </>
      )}
    </PageShell>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="font-display text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
function HeatCell({ v }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${RET_BG(v)}`}>{v}%</span>
  );
}
